import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../shared/store/hooks';
import { settingsSliceAPI } from '../../settings/store/settingsSlice';
import { Card, CardContent } from '../../../shared/components/ui/Card';
import Loader from '../../../shared/components/ui/Loader';
import { getPresenceChecklist, getScoreLabel } from '../services/onlinePresenceService';
import { fetchPresenceSummary, hasPresenceApiConfigured } from '../services/onlinePresenceApi';
import { FaCheckCircle, FaCircle, FaExternalLinkAlt, FaGlobe } from 'react-icons/fa';
import { createLogger } from '../../../shared/config/logger';

const logger = createLogger('OnlinePresencePage');

const SETTINGS_PATH = '/dashboard/settings/general';

/** Task IDs that can be fixed in LoyalPass Settings → General (website, address, Maps, social links). All other tasks require website changes or external actions. */
const TASK_IDS_FIXABLE_IN_SETTINGS = [
    'add-website',
    'add-google-maps',
    'add-address',
    'add-facebook',
    'add-instagram',
    'add-twitter',
    'add-linkedin',
];

export default function OnlinePresencePage() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const generalsettings = useAppSelector((state) => state.settings.generalsettings);
    const generalSettingsFetchStatus = useAppSelector((state) => state.settings.generalSettingsFetchStatus);
    const settingsLoading = useAppSelector((state) => state.settings.loading);

    const [apiData, setApiData] = useState(null);
    const [apiLoading, setApiLoading] = useState(hasPresenceApiConfigured);
    const [apiError, setApiError] = useState(null);
    const [useFallback, setUseFallback] = useState(!hasPresenceApiConfigured);

    useEffect(() => {
        if (generalSettingsFetchStatus === 'idle') {
            dispatch(settingsSliceAPI.fetchGeneralSettings());
        }
    }, [dispatch, generalSettingsFetchStatus]);

    useEffect(() => {
        if (!hasPresenceApiConfigured) {
            return;
        }
        let cancelled = false;
        setApiLoading(true);
        setApiError(null);
        fetchPresenceSummary()
            .then((data) => {
                if (!cancelled) {
                    setApiData(data);
                    setUseFallback(false);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    logger.warn('Presence API failed, using fallback', { error: err?.message });
                    setApiError(err?.response?.data?.message || err?.message || 'Could not load presence data');
                    setUseFallback(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setApiLoading(false);
                }
            });
        return () => { cancelled = true; };
    }, []);

    const fallbackChecklist = getPresenceChecklist(generalsettings);
    const fallbackScoreLabel = getScoreLabel(fallbackChecklist.score);
    const fallbackCompletedCount = fallbackChecklist.items.filter((i) => i.complete).length;

    const loading = hasPresenceApiConfigured ? (apiLoading && !apiData) : settingsLoading && !generalsettings?.name;

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[320px] p-6">
                <Loader />
            </div>
        );
    }

    const showApi = hasPresenceApiConfigured && apiData && !useFallback;

    return (
        <div className="space-y-4 md:space-y-6 max-w-4xl p-4 sm:p-6" data-tutorial="online-presence-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="min-w-0">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <FaGlobe className="text-primary shrink-0" />
                        <span className="truncate">Visibility Checker</span>
                    </h1>
                    <p className="text-gray-600 mt-1 text-sm sm:text-base">
                        {showApi
                            ? 'Website, SEO, and visibility insights from your business online presence.'
                            : 'See how your business looks online and what you can improve.'}
                    </p>
                </div>
            </div>

            {apiError && useFallback && hasPresenceApiConfigured && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm break-words">
                    Could not reach the presence service. Showing a simple checklist based on your settings.
                </div>
            )}

            {/* Score card */}
            <Card className="overflow-hidden">
                <CardContent className="p-4 sm:p-6">
                    {showApi ? (
                        <>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                                <div className="flex items-center gap-4 min-w-0">
                                    <div
                                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center text-xl sm:text-2xl font-bold text-white bg-primary shrink-0"
                                        aria-label={`Presence score: ${apiData.scores.total}%`}
                                    >
                                        {apiData.scores.total}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-500">Presence score</p>
                                        <p className="text-lg sm:text-xl font-semibold text-gray-900">
                                            {apiData.scores.total >= 85 ? 'Great' : apiData.scores.total >= 60 ? 'Good' : apiData.scores.total >= 30 ? 'Needs work' : 'Getting started'}
                                        </p>
                                        <p className="text-xs sm:text-sm text-gray-600 break-words mt-0.5">
                                            Website {apiData.scores.website} · SEO {apiData.scores.seo} · Google {apiData.scores.google} · Reviews {apiData.scores.reviews} · Visibility {apiData.scores.visibility}
                                        </p>
                                    </div>
                                </div>
                                <div className="w-full sm:flex-1 min-w-0">
                                    <div className="h-3 rounded-full bg-gray-200 overflow-hidden">
                                        <div
                                            className="h-full rounded-full bg-primary transition-all duration-500"
                                            style={{ width: `${Math.min(100, apiData.scores.total)}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                            {apiData.highlights?.length > 0 && (
                                <ul className="mt-4 space-y-1 text-sm text-gray-600">
                                    {apiData.highlights.map((h, i) => (
                                        <li key={i} className="flex items-start gap-2">
                                            <span className="text-primary mt-0.5 shrink-0">•</span>
                                            <span className="break-words">{h}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                                <div className="flex items-center gap-4 min-w-0">
                                    <div
                                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center text-xl sm:text-2xl font-bold text-white bg-primary shrink-0"
                                        aria-label={`Presence score: ${fallbackChecklist.score}%`}
                                    >
                                        {fallbackChecklist.score}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-500">Presence score</p>
                                        <p className="text-lg sm:text-xl font-semibold text-gray-900">{fallbackScoreLabel}</p>
                                        <p className="text-sm text-gray-600">
                                            {fallbackCompletedCount} of {fallbackChecklist.total} items complete
                                        </p>
                                    </div>
                                </div>
                                <div className="w-full sm:flex-1 min-w-0">
                                    <div className="h-3 rounded-full bg-gray-200 overflow-hidden">
                                        <div
                                            className="h-full rounded-full bg-primary transition-all duration-500"
                                            style={{ width: `${fallbackChecklist.score}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Checklist / Tasks */}
            <Card>
                <CardContent className="p-4 sm:p-6">
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                        {showApi ? 'Recommended actions' : 'Checklist'}
                    </h2>
                    <p className="text-gray-600 text-sm mb-4 sm:mb-6">
                        {showApi
                            ? 'Improve your business presence with these tasks. Many can be updated in Settings.'
                            : 'Filling these in helps customers find you and builds trust. Update them in Settings.'}
                    </p>
                    {showApi && apiData.tasks?.length > 0 ? (
                        <ul className="space-y-3 sm:space-y-4">
                            {apiData.tasks.map((task) => (
                                <li
                                    key={task.id}
                                    className="flex flex-col gap-3 p-3 sm:p-4 rounded-xl border border-gray-200 bg-gray-50/50 hover:bg-gray-50 transition"
                                >
                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                        <span className="mt-0.5 shrink-0">
                                            <span
                                                className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
                                                    task.impact === 'High' ? 'bg-red-100 text-red-800' : task.impact === 'Med' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'
                                                }`}
                                            >
                                                {task.impact}
                                            </span>
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-medium text-gray-900 text-sm sm:text-base">{task.title}</p>
                                            <p className="text-sm text-gray-600 mt-0.5 break-words">{task.description}</p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {task.category} {task.effort_minutes > 0 ? `· ~${task.effort_minutes} min` : ''}
                                            </p>
                                        </div>
                                    </div>
                                    {TASK_IDS_FIXABLE_IN_SETTINGS.includes(task.id) ? (
                                        <button
                                            type="button"
                                            onClick={() => navigate(SETTINGS_PATH)}
                                            className="w-full sm:w-auto shrink-0 text-sm font-medium text-primary hover:underline py-2.5 px-4 rounded-lg bg-primary/10 min-h-[44px] sm:min-h-0 flex items-center justify-center"
                                        >
                                            Go to Settings
                                        </button>
                                    ) : (
                                        <p className="shrink-0 text-sm text-gray-600">See description</p>
                                    )}
                                </li>
                            ))}
                        </ul>
                    ) : showApi && (!apiData.tasks || apiData.tasks.length === 0) ? (
                        <p className="text-gray-600 text-sm">No specific actions right now. Keep your details up to date in Settings.</p>
                    ) : (
                        <ul className="space-y-3 sm:space-y-4">
                            {fallbackChecklist.items.map((item) => (
                                <li
                                    key={item.id}
                                    className="flex flex-col gap-3 p-3 sm:p-4 rounded-xl border border-gray-200 bg-gray-50/50 hover:bg-gray-50 transition"
                                >
                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                        <span className="mt-0.5 text-lg shrink-0" aria-hidden>
                                            {item.complete ? (
                                                <FaCheckCircle className="text-green-600" />
                                            ) : (
                                                <FaCircle className="text-gray-300" />
                                            )}
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-medium text-gray-900 text-sm sm:text-base">{item.label}</p>
                                            <p className="text-sm text-gray-600 mt-0.5 break-words">{item.description}</p>
                                            {item.value && (
                                                <p className="text-xs sm:text-sm text-gray-500 mt-1 break-all">
                                                    {['website', 'review_link', 'facebook_url', 'instagram_url', 'twitter_url', 'linkedin_url'].includes(item.id) ? (
                                                        <a
                                                            href={item.value.startsWith('http') ? item.value : `https://${item.value}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-primary hover:underline inline-flex items-center gap-1 break-all"
                                                        >
                                                            <span className="break-all truncate max-w-full">{item.value}</span>
                                                            <FaExternalLinkAlt className="inline w-3 h-3 shrink-0" />
                                                        </a>
                                                    ) : (
                                                        <span className="break-words">{item.value}</span>
                                                    )}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => navigate(item.linkPath)}
                                        className="w-full sm:w-auto shrink-0 text-sm font-medium text-primary hover:underline py-2.5 px-4 rounded-lg bg-primary/10 min-h-[44px] sm:min-h-0 flex items-center justify-center"
                                    >
                                        {item.complete ? 'Edit' : 'Add'}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
